import React, { useContext, useState, useRef, useMemo, useEffect } from 'react';
import { ShopContext, MEN_CATEGORIES, WOMEN_CATEGORIES, POLO_TYPES, resolveProductImage, resolveProductThumbnail, getSlugFromFilters } from '../context/ShopContext';
import { SearchIcon, CartIcon, UserIcon, MoonIcon, SunIcon, SettingsIcon, CameraIcon, CloseIcon, ThreeDotsIcon, HeartIcon } from './Icons';
import { formatINR } from '../utils/currency';
import './Navbar.css';

const TRENDING_TAGS = [
  'Heavyweight Hoodie',
  'Midnight Silk Blended Polo',
  'Everyday Classic Pique Polo',
  'Ultra-Soft Comfort Stretch Polo',
  'Acid Wash Tee',
  'Sports Jersey',
  'Oxford Shirt'
];

const QUICK_COLLECTIONS = [
  { label: 'Blended Silk & Supima Polos', gender: 'Mens', subCategory: 'Blended Polo', icon: '💫' },
  { label: 'Everyday Classic Pique Polos', gender: 'Mens', subCategory: 'Everyday Polo', icon: '👔' },
  { label: 'Ultra-Soft Comfort Stretch Polos', gender: 'Mens', subCategory: 'Comfort Polo', icon: '☁️' },
  { label: 'Men Heavyweight Drops', gender: 'Mens', subCategory: 'Hoodies', icon: '🔥' },
  { label: 'Women Cropped & Hoodies', gender: 'Womens', subCategory: 'Hoodies', icon: '💎' },
  { label: 'Performance Sports Wear', gender: 'Sports', subCategory: 'Sports Crew Neck', icon: '⚡' }
];

const SPORTS_DECORATION_METHODS = ['Embroidery', 'DTF'];
const SPORTS_PRODUCT_TYPES = [
  { label: 'Crew Neck Jersey', subCategory: 'Sports Crew Neck', icon: '👕' },
  { label: 'Polo', subCategory: 'Sports Polo', icon: '⚡' }
];

const Navbar = ({ onCartClick }) => {
  const { 
    darkMode, 
    toggleDarkMode, 
    setView, 
    activeView, 
    cart, 
    wishlist = [],
    filters, 
    setFilters,
    user,
    products,
    videos,
    setAuthModalOpen,
    logoutUser,
    showToast
  } = useContext(ShopContext);

  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);
  const [expandedMenPolo, setExpandedMenPolo] = useState(true);
  const [expandedWomenPolo, setExpandedWomenPolo] = useState(true);
  const [mobileMenExpanded, setMobileMenExpanded] = useState(false);
  const [mobileWomenExpanded, setMobileWomenExpanded] = useState(false);
  const [mobileSportsExpanded, setMobileSportsExpanded] = useState(false);
  const [mobileMenPoloExpanded, setMobileMenPoloExpanded] = useState(true);
  const [mobileWomenPoloExpanded, setMobileWomenPoloExpanded] = useState(true);
  const [expandedSportsGroups, setExpandedSportsGroups] = useState({});
  const [mobileSportsGroups, setMobileSportsGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [showLiveDropdown, setShowLiveDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const shopItemRef = useRef(null);

  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);

  const debounceTimerRef = useRef(null);

  // Synchronize local search term with filter state
  useEffect(() => {
    setSearchTerm(filters.search || '');
  }, [filters.search]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Click outside listener and Escape key to close search dropdown
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowLiveDropdown(false);
        setSearchFocused(false);
      }
      if (shopItemRef.current && !shopItemRef.current.contains(e.target)) {
        setShopMenuOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowLiveDropdown(false);
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Instantaneous memoized search indexing across both Products and Videos
  const instantSearchResults = useMemo(() => {
    const rawQuery = (searchTerm || '').toLowerCase().trim();
    if (!rawQuery) {
      return { products: [], totalProductMatches: 0, videos: [], hasQuery: false };
    }

    const normalizedQuery = rawQuery.replace(/[-_]/g, ' ');
    const terms = Array.from(new Set([
      ...rawQuery.split(/\s+/),
      ...normalizedQuery.split(/\s+/)
    ])).filter(Boolean);

    const allMatchingProducts = (products || []).filter(product => {
      if (!product) return false;
      const pName = (product.name || '').toLowerCase();
      const pCat = (product.category || '').toLowerCase();
      const pGender = (product.gender || '').toLowerCase();
      const pDesc = (product.description || '').toLowerCase();
      const pTag = (product.tag || '').toLowerCase();
      const pColors = Array.isArray(product.colors) ? product.colors.join(' ').toLowerCase() : '';
      const pSpecs = Array.isArray(product.specifications)
        ? product.specifications.map(s => `${s.key || ''} ${s.value || ''}`).join(' ').toLowerCase()
        : '';
      const pType = (product.poloType || '').toLowerCase();

      return terms.some(t =>
        pName.includes(t) ||
        pCat.includes(t) ||
        pGender.includes(t) ||
        pDesc.includes(t) ||
        pTag.includes(t) ||
        pColors.includes(t) ||
        pSpecs.includes(t) ||
        pType.includes(t)
      );
    });

    const matchedVideos = (videos || []).filter(video => {
      if (!video) return false;
      const vTitle = (video.title || '').toLowerCase();
      const vSub = (video.subtitle || '').toLowerCase();
      const vTag = (video.tag || '').toLowerCase();
      return terms.some(t => vTitle.includes(t) || vSub.includes(t) || vTag.includes(t));
    }).slice(0, 2);

    const topProducts = allMatchingProducts.slice(0, 5);

    // Preload top matched images in browser memory for instant 0ms visual rendering
    if (typeof window !== 'undefined' && topProducts.length > 0) {
      topProducts.forEach(prod => {
        try {
          const imgUrl = resolveProductThumbnail ? resolveProductThumbnail(prod) : resolveProductImage(prod);
          if (imgUrl) {
            const preloadImg = new Image();
            preloadImg.src = imgUrl;
          }
        } catch (e) {}
      });
    }

    return { 
      products: topProducts, 
      totalProductMatches: allMatchingProducts.length,
      videos: matchedVideos, 
      hasQuery: true 
    };
  }, [searchTerm, products, videos]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setShowLiveDropdown(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setFilters(prev => ({ 
        ...prev, 
        search: val,
        ...(val.trim() ? { category: 'All', gender: 'All', subCategory: 'All' } : {})
      }));
    }, 120);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setShowLiveDropdown(false);
    setFilters(prev => ({ 
      ...prev, 
      search: searchTerm,
      ...(searchTerm.trim() ? { category: 'All', gender: 'All', subCategory: 'All' } : {})
    }));
    if (activeView !== 'shop') {
      setView('shop');
    }
  };

  const handleSelectProduct = (product) => {
    setShowLiveDropdown(false);
    setView('product-detail', product);
  };

  const handleSelectVideo = () => {
    setShowLiveDropdown(false);
    setView('home');
    setTimeout(() => {
      const showcase = document.querySelector('.video-showcase-section');
      if (showcase) {
        showcase.scrollIntoView({ behavior: 'smooth' });
      }
    }, 150);
  };

  const handleImageSearchClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageSearchUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      if (showToast) showToast('Please select a valid image file for visual search.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      const rawName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      const isGeneric = /^(img|image|screenshot|photo|whatsapp|pic|dsc|frame|capture)[\d\s_-]*/i.test(rawName.trim());
      const cleanKeyword = isGeneric ? '' : rawName;

      setSearchTerm(cleanKeyword);
      setFilters(prev => ({
        ...prev,
        imageSearchPreview: dataUrl,
        imageSearchName: file.name,
        search: cleanKeyword,
        category: 'All',
        gender: 'All',
        subCategory: 'All'
      }));

      if (activeView !== 'shop') {
        setView('shop');
      }

      if (showToast) {
        showToast('📸 Visual search active — matching items found!', 'success');
      }
    };
    reader.readAsDataURL(file);
  };

  const clearImageSearch = (e) => {
    if (e) e.stopPropagation();
    if (filters.imageSearchPreview && filters.imageSearchPreview.startsWith('blob:')) {
      URL.revokeObjectURL(filters.imageSearchPreview);
    }
    setSearchTerm('');
    setFilters(prev => ({
      ...prev,
      imageSearchPreview: null,
      imageSearchName: '',
      search: ''
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCategoryNav = (category) => {
    if (filters.imageSearchPreview && filters.imageSearchPreview.startsWith('blob:')) {
      URL.revokeObjectURL(filters.imageSearchPreview);
    }
    setSearchTerm('');
    let gender = 'All';
    let subCategory = 'All';
    if (category === 'Mens') {
      gender = 'Mens';
    } else if (category === 'Womens') {
      gender = 'Womens';
    } else if (category !== 'All') {
      subCategory = category;
    }
    setFilters(prev => ({
      ...prev,
      category: 'All',
      gender,
      subCategory,
      search: '',
      imageSearchPreview: null,
      imageSearchName: '',
      size: 'All',
      color: 'All',
      rating: 'All',
      priceRange: 10000
    }));
    setShopMenuOpen(false);
    setMobileMenuOpen(false);
    setView('shop');
    try {
      window.history.pushState({ view: 'shop', category }, document.title, '/shop');
    } catch (e) {
      // Fallback
    }
  };

  const handleSubCategoryNav = (gender, subCategory) => {
    if (filters.imageSearchPreview && filters.imageSearchPreview.startsWith('blob:')) {
      URL.revokeObjectURL(filters.imageSearchPreview);
    }
    setSearchTerm('');
    setFilters(prev => ({
      ...prev,
      category: 'All',
      gender: gender || 'All',
      subCategory: subCategory || 'All',
      search: '',
      imageSearchPreview: null,
      imageSearchName: '',
      size: 'All',
      color: 'All',
      rating: 'All',
      priceRange: 10000
    }));
    setShopMenuOpen(false);
    setMobileMenuOpen(false);
    setView('shop');
    const slug = getSlugFromFilters(gender, subCategory);
    const targetPath = slug ? `/shop/${slug}` : '/shop';
    try {
      window.history.pushState({ view: 'shop', gender, subCategory }, document.title, targetPath);
    } catch (e) {
      // Fallback
    }
  };

  return (
    <header className="navbar-container">
      <nav className="navbar glass">
        <div className="navbar-content container">
          {/* Left Side: 3-Dot Mobile Menu Button on Left Side Corner + Logo */}
          <div className="navbar-left-section">
            <button 
              className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation Menu"
              title="Menu"
            >
              {mobileMenuOpen ? <CloseIcon size={20} /> : <ThreeDotsIcon size={22} />}
            </button>

            <div className="navbar-brand" onClick={() => { setView('home'); setMobileMenuOpen(false); }}>
              <img 
                src="/pallywear-logo.png" 
                alt="PallyWear Logo" 
                className="navbar-brand-logo" 
                onError={(e) => {
                  if (!e.target.dataset.tried) {
                    e.target.dataset.tried = '1';
                    e.target.src = '/build/pallywear-logo.png';
                  }
                }}
              />
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <ul className="nav-links">
            <li 
              className={activeView === 'home' ? 'active' : ''} 
              onClick={() => setView('home')}
            >
              Home
            </li>
            
            {/* Shop Nav Item with Direct Click & Mega-Menu Toggle */}
            <li 
              ref={shopItemRef}
              className={`nav-item-shop ${activeView === 'shop' ? 'active' : ''}`} 
            >
              <span 
                className="nav-shop-label"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryNav('All');
                  setShopMenuOpen(false);
                }}
              >
                Shop
              </span>
              <span 
                className={`shop-arrow-indicator ${shopMenuOpen ? 'open' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShopMenuOpen(prev => !prev);
                }}
                title="Toggle collections menu"
              >
                ▾
              </span>

              {/* Shop Mega Menu Dropdown */}
              {shopMenuOpen && (
                <div 
                  className="shop-megamenu-panel glass animate-fade-in"
                >
                  {/* All Drops Quick Link */}
                  <div 
                    style={{
                      padding: '0.6rem 0.85rem',
                      marginBottom: '1rem',
                      borderRadius: '8px',
                      background: 'linear-gradient(90deg, rgba(0, 112, 243, 0.12), rgba(168, 85, 247, 0.12))',
                      border: '1px solid rgba(0, 112, 243, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                      handleCategoryNav('All');
                      setShopMenuOpen(false);
                    }}
                  >
                    <span style={{ fontWeight: '700', fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      ✨ View All Drops & Collections
                    </span>
                    <span style={{ fontSize: '0.78rem', color: '#60a5fa', fontWeight: '600' }}>Browse All →</span>
                  </div>

                  <div className="megamenu-inner-grid megamenu-three-cols">
                    {/* Men Column */}
                    <div className="megamenu-col">
                      <h4 
                        className="megamenu-col-title"
                        onClick={() => {
                          handleSubCategoryNav('Mens', 'All');
                          setShopMenuOpen(false);
                        }}
                      >
                        Men
                      </h4>
                      <ul className="megamenu-items-list">
                        {MEN_CATEGORIES.map(item => {
                          if (item === 'Polos') {
                            return (
                              <li key="men-polo-group" className="megamenu-group-item">
                                <div 
                                  className="megamenu-polo-header"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedMenPolo(prev => !prev);
                                  }}
                                  title="Click to toggle Polo styles"
                                >
                                  <span className="megamenu-polo-title">Polos</span>
                                  <span className={`polo-expand-badge ${expandedMenPolo ? 'expanded' : ''}`}>
                                    {expandedMenPolo ? '▴' : '▾'}
                                  </span>
                                </div>
                                {expandedMenPolo && (
                                  <ul className="megamenu-nested-sublist animate-fade-in">
                                    <li 
                                      className="megamenu-nested-item all-polos-link"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSubCategoryNav('Mens', 'Polos');
                                        setShopMenuOpen(false);
                                      }}
                                    >
                                      <span className="nested-item-icon">👕</span>
                                      <span className="nested-item-name">All Polos</span>
                                    </li>
                                    {POLO_TYPES.map(poloType => {
                                      const icon = poloType === 'Blended Polo' ? '💫' : poloType === 'Everyday Polo' ? '👔' : '☁️';
                                      return (
                                        <li 
                                          key={`men-polo-${poloType}`}
                                          className="megamenu-nested-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubCategoryNav('Mens', poloType);
                                            setShopMenuOpen(false);
                                          }}
                                        >
                                          <span className="nested-item-icon">{icon}</span>
                                          <span className="nested-item-name">{poloType}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </li>
                            );
                          }
                          return (
                            <li 
                              key={item} 
                              className="megamenu-item-link"
                              onClick={() => {
                                handleSubCategoryNav('Mens', item);
                                setShopMenuOpen(false);
                              }}
                            >
                              {item}
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* Women Column */}
                    <div className="megamenu-col">
                      <h4 
                        className="megamenu-col-title"
                        onClick={() => {
                          handleSubCategoryNav('Womens', 'All');
                          setShopMenuOpen(false);
                        }}
                      >
                        Women
                      </h4>
                      <ul className="megamenu-items-list">
                        {WOMEN_CATEGORIES.map(item => {
                          if (item === 'Polos') {
                            return (
                              <li key="women-polo-group" className="megamenu-group-item">
                                <div 
                                  className="megamenu-polo-header"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedWomenPolo(prev => !prev);
                                  }}
                                  title="Click to toggle Polo styles"
                                >
                                  <span className="megamenu-polo-title">Polos</span>
                                  <span className={`polo-expand-badge ${expandedWomenPolo ? 'expanded' : ''}`}>
                                    {expandedWomenPolo ? '▴' : '▾'}
                                  </span>
                                </div>
                                {expandedWomenPolo && (
                                  <ul className="megamenu-nested-sublist animate-fade-in">
                                    <li 
                                      className="megamenu-nested-item all-polos-link"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSubCategoryNav('Womens', 'Polos');
                                        setShopMenuOpen(false);
                                      }}
                                    >
                                      <span className="nested-item-icon">👕</span>
                                      <span className="nested-item-name">All Polos</span>
                                    </li>
                                    {POLO_TYPES.map(poloType => {
                                      const icon = poloType === 'Blended Polo' ? '💫' : poloType === 'Everyday Polo' ? '👔' : '☁️';
                                      return (
                                        <li 
                                          key={`women-polo-${poloType}`}
                                          className="megamenu-nested-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubCategoryNav('Womens', poloType);
                                            setShopMenuOpen(false);
                                          }}
                                        >
                                          <span className="nested-item-icon">{icon}</span>
                                          <span className="nested-item-name">{poloType}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </li>
                            );
                          }
                          return (
                            <li 
                              key={item} 
                              className="megamenu-item-link"
                              onClick={() => {
                                handleSubCategoryNav('Womens', item);
                                setShopMenuOpen(false);
                              }}
                            >
                              {item}
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* Sports Column */}
                    <div className="megamenu-col">
                      <h4 
                        className="megamenu-col-title"
                        onClick={() => {
                          handleSubCategoryNav('Sports', 'All');
                          setShopMenuOpen(false);
                        }}
                      >
                        Sports
                      </h4>
                      <ul className="megamenu-items-list">
                        {SPORTS_DECORATION_METHODS.map(method => (
                          <li key={method} className="megamenu-group-item">
                            <div
                              className="megamenu-polo-header"
                              role="button"
                              tabIndex={0}
                              aria-expanded={Boolean(expandedSportsGroups[method])}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSportsGroups(prev => ({ ...prev, [method]: !prev[method] }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setExpandedSportsGroups(prev => ({ ...prev, [method]: !prev[method] }));
                                }
                              }}
                            >
                              <span className="megamenu-polo-title">{method}</span>
                              <span className={`polo-expand-badge ${expandedSportsGroups[method] ? 'expanded' : ''}`}>
                                {expandedSportsGroups[method] ? '▴' : '▾'}
                              </span>
                            </div>
                            {expandedSportsGroups[method] && (
                              <ul className="megamenu-nested-sublist animate-fade-in">
                                {SPORTS_PRODUCT_TYPES.map(type => (
                                  <li
                                    key={`${method}-${type.subCategory}`}
                                    className="megamenu-nested-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubCategoryNav('Sports', type.subCategory);
                                    }}
                                  >
                                    <span className="nested-item-icon">{type.icon}</span>
                                    <span className="nested-item-name">{type.label}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </li>

            <li 
              className={activeView === 'about' ? 'active' : ''} 
              onClick={() => setView('about')}
            >
              About
            </li>
            <li 
              className={activeView === 'blog' || activeView === 'blog-post' ? 'active' : ''} 
              onClick={() => setView('blog')}
            >
              Blog
            </li>
            <li 
              className={activeView === 'account' ? 'active' : ''} 
              onClick={() => setView('account')}
            >
              Account
            </li>
            <li 
              className={activeView === 'track-order' ? 'active' : ''} 
              onClick={() => setView('track-order')}
            >
              Track Order
            </li>
          </ul>

          {/* Action Icons */}
          <div className="nav-actions">
            {/* Search Input Bar with Instant Visual & Video Results */}
            <div 
              ref={searchWrapperRef}
              className={`search-bar-wrapper ${searchFocused ? 'focused' : ''} ${filters.imageSearchPreview ? 'has-visual-search' : ''}`}
            >
              <form onSubmit={handleSearchSubmit} className="search-form-wrapper" style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <SearchIcon className="search-bar-icon" size={18} />
                
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageSearchUpload}
                  style={{ display: 'none' }}
                />

                {filters.imageSearchPreview ? (
                  <div className="search-image-badge" title={`Visual Search: ${filters.imageSearchName || 'Uploaded Image'}`}>
                    <img src={filters.imageSearchPreview} alt="Search Preview" className="search-badge-img" />
                    <button 
                      type="button" 
                      className="clear-image-search-btn" 
                      onClick={clearImageSearch}
                      title="Remove Image Search"
                    >
                      <CloseIcon size={12} />
                    </button>
                  </div>
                ) : null}

                <input
                  type="text"
                  placeholder={filters.imageSearchPreview ? "Visual active..." : "Search products & videos..."}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => { setSearchFocused(true); setShowLiveDropdown(true); }}
                  className="search-input"
                />

                <button
                  type="button"
                  className={`camera-search-btn ${filters.imageSearchPreview ? 'active' : ''}`}
                  onClick={filters.imageSearchPreview ? clearImageSearch : handleImageSearchClick}
                  title={filters.imageSearchPreview ? "Clear Visual Search" : "Upload picture to search"}
                >
                  {filters.imageSearchPreview ? <CloseIcon size={16} /> : <CameraIcon size={18} />}
                </button>
              </form>

              {/* Instant Live Search Results & Spotlight Discover Hub */}
              {showLiveDropdown && (
                <div className="instant-search-dropdown glass animate-fade-in">
                  {!instantSearchResults.hasQuery ? (
                    /* 🌟 Spotlight Quick Discover Hub when search is empty */
                    <div className="spotlight-discover-hub">
                      <div className="spotlight-section">
                        <div className="spotlight-header">
                          <span className="spotlight-badge">🔥 TRENDING SEARCHES</span>
                        </div>
                        <div className="trending-tags-grid">
                          {TRENDING_TAGS.map(tag => (
                            <button
                              key={tag}
                              type="button"
                              className="trending-tag-pill"
                              onClick={() => {
                                setSearchTerm(tag);
                                setShowLiveDropdown(false);
                                setSearchFocused(false);
                                setFilters(prev => ({
                                  ...prev,
                                  category: 'All',
                                  gender: 'All',
                                  subCategory: 'All',
                                  search: tag,
                                  imageSearchPreview: null,
                                  imageSearchName: ''
                                }));
                                if (activeView !== 'shop') setView('shop');
                              }}
                            >
                              <SearchIcon size={12} className="tag-search-icon" />
                              <span>{tag}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="spotlight-section">
                        <div className="spotlight-header">
                          <span className="spotlight-badge">✨ CURATED DROPS</span>
                        </div>
                        <div className="quick-collections-list">
                          {QUICK_COLLECTIONS.map(col => (
                            <div
                              key={col.label}
                              className="quick-col-item"
                              onClick={() => {
                                handleSubCategoryNav(col.gender, col.subCategory);
                                setShowLiveDropdown(false);
                                setSearchFocused(false);
                              }}
                            >
                              <span className="quick-col-icon">{col.icon}</span>
                              <span className="quick-col-label">{col.label}</span>
                              <span className="quick-col-arrow">→</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Visual Search Highlight Banner */}
                      <div 
                        className="spotlight-visual-banner"
                        onClick={handleImageSearchClick}
                      >
                        <div className="visual-banner-left">
                          <CameraIcon size={18} className="visual-banner-icon" />
                          <div className="visual-banner-text">
                            <strong>Visual Smart Search</strong>
                            <span>Upload a photo to find matching pieces</span>
                          </div>
                        </div>
                        <span className="visual-banner-btn">Upload ↗</span>
                      </div>
                    </div>
                  ) : (
                    /* ⚡ Instant Results for Active Query */
                    <div className="live-search-results-block">
                      <div className="search-dropdown-header">
                        <span>⚡ Instant Results for "<strong>{searchTerm}</strong>"</span>
                        <span className="results-count-pill">{instantSearchResults.products.length} found</span>
                      </div>

                      {/* Matching Products Section */}
                      {instantSearchResults.products.length > 0 && (
                        <div className="search-dropdown-section">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                            <span className="dropdown-section-title">👕 PRODUCTS ({instantSearchResults.totalProductMatches || instantSearchResults.products.length})</span>
                            <span 
                              style={{ fontSize: '0.75rem', color: '#6366f1', cursor: 'pointer', fontWeight: '600' }}
                              onClick={handleSearchSubmit}
                            >
                              View all in Shop →
                            </span>
                          </div>
                          <div className="search-products-list">
                            {instantSearchResults.products.map(p => {
                              const itemImg = resolveProductThumbnail ? resolveProductThumbnail(p) : resolveProductImage(p);
                              return (
                                <div 
                                  key={p.id} 
                                  className="search-product-item"
                                  onClick={() => handleSelectProduct(p)}
                                >
                                  <img 
                                    src={itemImg} 
                                    alt={p.name} 
                                    className="search-item-thumb" 
                                    loading="eager"
                                    decoding="async"
                                    width="48"
                                    height="48"
                                    onError={(e) => { e.target.src = '/images/cat-slide-men-polo.webp'; }}
                                  />
                                  <div className="search-item-info">
                                    <span className="search-item-title">{p.name}</span>
                                    <div className="search-item-meta">
                                      <span className="search-item-price">{formatINR(p.price)}</span>
                                      <span className="search-item-cat">{p.category}</span>
                                      {p.stock > 0 && <span className="search-stock-badge">In Stock</span>}
                                    </div>
                                  </div>
                                  <span className="search-item-arrow">→</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Matching Videos Section */}
                      {instantSearchResults.videos.length > 0 && (
                        <div className="search-dropdown-section">
                          <span className="dropdown-section-title">🎥 MOTION CLIPS ({instantSearchResults.videos.length})</span>
                          <div className="search-videos-list">
                            {instantSearchResults.videos.map(v => (
                              <div 
                                key={v.id} 
                                className="search-video-item"
                                onClick={handleSelectVideo}
                              >
                                <div className="search-video-thumb-wrapper">
                                  <video src={v.src} muted playsInline className="search-video-thumb" />
                                  <span className="search-play-badge">▶</span>
                                </div>
                                <div className="search-item-info">
                                  <span className="search-item-title">{v.title}</span>
                                  <span className="search-item-subtitle">{v.subtitle || v.tag}</span>
                                </div>
                                <span className="search-item-arrow">→</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {instantSearchResults.products.length === 0 && instantSearchResults.videos.length === 0 && (
                        <div className="search-empty-state">
                          <p className="empty-search-msg">No exact match for "<strong>{searchTerm}</strong>".</p>
                          <span className="empty-try-txt">Try searching for:</span>
                          <div className="empty-search-tags">
                            {TRENDING_TAGS.slice(0, 3).map(tag => (
                              <button
                                key={tag}
                                type="button"
                                className="trending-tag-pill"
                                onClick={() => {
                                  setSearchTerm(tag);
                                  setFilters(prev => ({ ...prev, search: tag }));
                                }}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer Action */}
                      <div className="search-dropdown-footer" onClick={handleSearchSubmit}>
                        <span>Explore full matching collection in Shop</span>
                        <span>→</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Desktop actions row (Hidden on Mobile) */}
            <div className="desktop-actions">
              {user && user.role === 'admin' && (
                <button 
                  className={`btn-icon ${activeView === 'admin' ? 'active-icon' : ''}`}
                  title="Admin Dashboard"
                  onClick={() => setView('admin')}
                >
                  <SettingsIcon size={20} />
                </button>
              )}

              <button 
                onClick={toggleDarkMode} 
                className="btn-icon theme-toggle-btn"
                title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {darkMode ? <SunIcon size={20} /> : <MoonIcon size={20} />}
              </button>

              {/* Wishlist Trigger */}
              <button 
                onClick={() => setView('account')} 
                className="btn-icon wishlist-indicator"
                title={`Wishlist (${wishlist ? wishlist.length : 0} items)`}
              >
                <HeartIcon 
                  size={20} 
                  fill={wishlist && wishlist.length > 0 ? "#ef4444" : "none"} 
                  stroke={wishlist && wishlist.length > 0 ? "#ef4444" : "currentColor"} 
                />
                {wishlist && wishlist.length > 0 && (
                  <span className="cart-badge wishlist-badge">{wishlist.length}</span>
                )}
              </button>

              {user ? (
                <button 
                  className="btn-icon profile-btn user-logged-in-badge"
                  onClick={logoutUser}
                  title={`Logged in as ${user.fullName || user.email || 'User'}. Click to Log Out`}
                >
                  <span className="navbar-user-initials">
                    {(user.fullName || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                </button>
              ) : (
                <button 
                  className="btn-icon profile-btn"
                  onClick={() => setAuthModalOpen(true)}
                  title="Login / Register"
                >
                  <UserIcon size={20} />
                </button>
              )}
            </div>

            {/* Shopping Cart Drawer Trigger (Always Visible) */}
            <button 
              onClick={onCartClick} 
              className="btn-icon cart-indicator"
              title="Shopping Cart"
            >
              <CartIcon size={20} />
              {cartItemsCount > 0 && (
                <span className="cart-badge">{cartItemsCount}</span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu backdrop overlay */}
        {mobileMenuOpen && (
          <div className="mobile-menu-backdrop" onClick={() => setMobileMenuOpen(false)} />
        )}

        {/* Mobile Navigation Dropdown Menu */}
        <div className={`mobile-nav-dropdown ${mobileMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-links">
            <li className={activeView === 'home' ? 'active' : ''} onClick={() => { setView('home'); setMobileMenuOpen(false); }}>
              Home
            </li>
            <li className={activeView === 'shop' ? 'active' : ''} onClick={() => { handleCategoryNav('All'); setMobileMenuOpen(false); }}>
              Shop All Drops
            </li>

            {/* Mobile Men Categories */}
            <li className="mobile-category-group">
              <div 
                className="mobile-group-header" 
                onClick={() => setMobileMenExpanded(!mobileMenExpanded)}
              >
                <span>Men</span>
                <span>{mobileMenExpanded ? '−' : '+'}</span>
              </div>
              {mobileMenExpanded && (
                <ul className="mobile-sub-cat-list animate-fade-in">
                  {MEN_CATEGORIES.map(sub => {
                    if (sub === 'Polos') {
                      return (
                        <li key="mob-men-polos" className="mobile-sub-group-item">
                          <div 
                            className="mobile-sub-group-header"
                            onClick={() => setMobileMenPoloExpanded(!mobileMenPoloExpanded)}
                          >
                            <span>Polos</span>
                            <span className="mobile-sub-indicator">{mobileMenPoloExpanded ? '▴' : '▾'}</span>
                          </div>
                          {mobileMenPoloExpanded && (
                            <ul className="mobile-nested-sub-list animate-fade-in">
                              <li 
                                className="mobile-nested-sub-item"
                                onClick={() => handleSubCategoryNav('Mens', 'Polos')}
                              >
                                👕 All Polos
                              </li>
                              {POLO_TYPES.map(pType => (
                                <li 
                                  key={`mob-men-${pType}`}
                                  className="mobile-nested-sub-item"
                                  onClick={() => handleSubCategoryNav('Mens', pType)}
                                >
                                  {pType === 'Blended Polo' ? '💫' : pType === 'Everyday Polo' ? '👔' : '☁️'} {pType}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    }
                    return (
                      <li 
                        key={`mob-men-${sub}`}
                        onClick={() => handleSubCategoryNav('Mens', sub)}
                        className="mobile-sub-item"
                      >
                        {sub}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>

            {/* Mobile Women Categories */}
            <li className="mobile-category-group">
              <div 
                className="mobile-group-header" 
                onClick={() => setMobileWomenExpanded(!mobileWomenExpanded)}
              >
                <span>Women</span>
                <span>{mobileWomenExpanded ? '−' : '+'}</span>
              </div>
              {mobileWomenExpanded && (
                <ul className="mobile-sub-cat-list animate-fade-in">
                  {WOMEN_CATEGORIES.map(sub => {
                    if (sub === 'Polos') {
                      return (
                        <li key="mob-women-polos" className="mobile-sub-group-item">
                          <div 
                            className="mobile-sub-group-header"
                            onClick={() => setMobileWomenPoloExpanded(!mobileWomenPoloExpanded)}
                          >
                            <span>Polos</span>
                            <span className="mobile-sub-indicator">{mobileWomenPoloExpanded ? '▴' : '▾'}</span>
                          </div>
                          {mobileWomenPoloExpanded && (
                            <ul className="mobile-nested-sub-list animate-fade-in">
                              <li 
                                className="mobile-nested-sub-item"
                                onClick={() => handleSubCategoryNav('Womens', 'Polos')}
                              >
                                👕 All Polos
                              </li>
                              {POLO_TYPES.map(pType => (
                                <li 
                                  key={`mob-women-${pType}`}
                                  className="mobile-nested-sub-item"
                                  onClick={() => handleSubCategoryNav('Womens', pType)}
                                >
                                  {pType === 'Blended Polo' ? '💫' : pType === 'Everyday Polo' ? '👔' : '☁️'} {pType}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    }
                    return (
                      <li 
                        key={`mob-women-${sub}`}
                        onClick={() => handleSubCategoryNav('Womens', sub)}
                        className="mobile-sub-item"
                      >
                        {sub}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>

            {/* Mobile Sports Categories */}
            <li className="mobile-category-group">
              <div 
                className="mobile-group-header" 
                onClick={() => setMobileSportsExpanded(!mobileSportsExpanded)}
              >
                <span>Sports</span>
                <span>{mobileSportsExpanded ? '−' : '+'}</span>
              </div>
              {mobileSportsExpanded && (
                <ul className="mobile-sub-cat-list animate-fade-in">
                  {SPORTS_DECORATION_METHODS.map(method => (
                    <li key={`mob-sports-${method}`} className="mobile-sub-group-item">
                      <div
                        className="mobile-sub-group-header"
                        role="button"
                        tabIndex={0}
                        aria-expanded={Boolean(mobileSportsGroups[method])}
                        onClick={() => setMobileSportsGroups(prev => ({ ...prev, [method]: !prev[method] }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setMobileSportsGroups(prev => ({ ...prev, [method]: !prev[method] }));
                          }
                        }}
                      >
                        <span>{method}</span>
                        <span className="mobile-sub-indicator">{mobileSportsGroups[method] ? '−' : '+'}</span>
                      </div>
                      {mobileSportsGroups[method] && (
                        <ul className="mobile-nested-sub-list animate-fade-in">
                          {SPORTS_PRODUCT_TYPES.map(type => (
                            <li
                              key={`mob-${method}-${type.subCategory}`}
                              onClick={() => handleSubCategoryNav('Sports', type.subCategory)}
                              className="mobile-nested-sub-item"
                            >
                              {type.icon} {type.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>

            <li className={activeView === 'about' ? 'active' : ''} onClick={() => { setView('about'); setMobileMenuOpen(false); }}>
              About
            </li>
            <li className={activeView === 'blog' || activeView === 'blog-post' ? 'active' : ''} onClick={() => { setView('blog'); setMobileMenuOpen(false); }}>
              Blog & Journal
            </li>
            <li className={activeView === 'account' ? 'active' : ''} onClick={() => { setView('account'); setMobileMenuOpen(false); }}>
              Account
            </li>
            <li className={activeView === 'track-order' ? 'active' : ''} onClick={() => { setView('track-order'); setMobileMenuOpen(false); }}>
              Track Order
            </li>
          </ul>

          <div className="mobile-actions-divider"></div>

          {/* Quick Actions Grid inside Mobile Dropdown: Theme, Logon, Cart */}
          <div className="mobile-quick-actions">
            <button 
              onClick={() => { toggleDarkMode(); }} 
              className="mobile-action-card"
              title="Toggle Dark / Light Theme"
            >
              <div className="mobile-action-icon">
                {darkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />}
              </div>
              <span className="mobile-action-label">{darkMode ? "Light Theme" : "Dark Theme"}</span>
            </button>

            {user ? (
              <button 
                className="mobile-action-card logout-card" 
                onClick={() => { logoutUser(); setMobileMenuOpen(false); }}
                title="Log Out"
              >
                <div className="mobile-action-icon user-initial-circle">
                  {(user.fullName || user.email || 'U').charAt(0).toUpperCase()}
                </div>
                <span className="mobile-action-label">Log Out</span>
              </button>
            ) : (
              <button 
                className="mobile-action-card" 
                onClick={() => { setAuthModalOpen(true); setMobileMenuOpen(false); }}
                title="Log In / Register"
              >
                <div className="mobile-action-icon">
                  <UserIcon size={18} />
                </div>
                <span className="mobile-action-label">Log In</span>
              </button>
            )}

            <button 
              className="mobile-action-card cart-card" 
              onClick={() => { onCartClick(); setMobileMenuOpen(false); }}
              title="Shopping Cart"
            >
              <div className="mobile-action-icon cart-icon-relative">
                <CartIcon size={18} />
                {cartItemsCount > 0 && <span className="mobile-cart-badge-pill">{cartItemsCount}</span>}
              </div>
              <span className="mobile-action-label">Cart ({cartItemsCount})</span>
            </button>

            {user && user.role === 'admin' && (
              <button 
                className="mobile-action-card"
                onClick={() => { setView('admin'); setMobileMenuOpen(false); }}
                title="Admin Panel"
              >
                <div className="mobile-action-icon">
                  <SettingsIcon size={18} />
                </div>
                <span className="mobile-action-label">Admin</span>
              </button>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;

